import React, { useContext, useEffect, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useSelector } from "react-redux";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import useColorScheme from "../../hooks/useColorScheme";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination2";
import { TouchableOpacity } from "react-native-gesture-handler";
import { headers, mdHeaders, smHeaders } from "./utils/operationHeaders";
import {
  mdNestedHeaders as ordersMdHeaders,
  nestedHeaders as ordersHeader,
  smNestedHeaders as ordersSmHeaders,
} from "./utils/nestedHeaders";

import { ordersCollectionModel } from "../../models/ordersModel";
import {
  getAllOperations,
  retrieveAllOperationUsers,
  retrieveRecentOrders,
  retrieveRecentUpdatedOrders,
} from "../../services/operationService";
import { OperationLabelBannerWithSearchTags } from "../../components/custom/Banners/OperationLabelBannerWithSearchTags";
import Spinner from "../../components/shared/Spinner";
import { SearchOperationLabelBannerWithSearchBar } from "../../components/custom/Banners/SearchOperationLabelBannerWithSearchBar";
import { Button } from "react-native-paper";
import { useToast } from "react-native-toast-notifications";
import OperationProcessingUserModal from "./partials/OperationProcessingUserModal";
import OperationStatusUpdateModal from "./partials/OperationStatusUpdateModal";

export default function OperationsManagementScreen({ route, navigation }: any) {
  const colorScheme = useColorScheme();
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>();
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<string>("");
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const operationsFilterCategoryList = [
    "All",
    "Draft",
    "Pending",
    "Cancelled",
    "Processed",
    "Dispatched",
    "Completed",
    "Last 3 months",
  ];

  const { operationsList, isOperationsListLoaded } = useSelector(
    (state: any) => state.operations
  );

  const combinedOrders = operationsList.reduce((acc, item) => {
    if (item.orders && Array.isArray(item.orders)) {
      return acc.concat(item.orders);
    }
    return acc;
  }, []);

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const [_filterCategory, _setFilterCategory] = useState<string>("All");
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      ordersHeader
    );
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const isUserAdmin = true;
  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Packing #",
    "Hubspot ID",
    "Document Number",
    "Customer Number",
    "SAP Document No",
  ];
  const [disableHeader, setDisableHeader] = useState<boolean>(false);

  const handleSearch = () => {
    return true;
  };
  const onHandleRowClick = () => {
    return true;
  };
  const [selected, setSelected] = useState<ordersCollectionModel>(
    {} as unknown as ordersCollectionModel
  );
  const onHandleDeleteRow = () => {
    return true;
  };
  const onHandleSortColumn = () => {
    return true;
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "process_person" || action === "update_status") {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    } else {
      navigation.navigate("SingleOperation", { order, action });
    }
  };

  useEffect(() => {
    const headersToUse = isLargeDevice
      ? headers
      : isMediumDevice
        ? mdHeaders
        : smHeaders;

    const nestedHeadersToUse = isLargeDevice
      ? ordersHeader
      : isMediumDevice
        ? ordersMdHeaders
        : ordersSmHeaders;

    setTableHeaders(headersToUse);
    setNestedTableHeaders(nestedHeadersToUse);
    setDisableHeader(isSmallDevice);

    if (isFocused || isRefresh) {
      getAllOperations(headersToUse);
      retrieveAllOperationUsers();
      setRefresh(false);
    }
  }, [isFocused, isRefresh, isLargeDevice, isMediumDevice, isSmallDevice]);

  const PageHeader = () => {
    if (isSmallDevice) return <SmPageHeader />;
    else return <DefaultPageHeader />;
  };

  const [loadingHubspot, setLoadingHubspot] = useState<boolean>(false);
  const toast = useToast();
  const handleRetrieveRecentOrders = async () => {
    setLoadingHubspot(true);
    await retrieveRecentOrders()
      .then(() => {
        getAllOperations(tableHeaders);
        setLoadingHubspot(false);
        toast.show("Orders has been queued for processing...", {
          type: "success",
        });
      })
      .catch((err) => {
        setLoadingHubspot(false);
        toast.show("Orders has been queued for processing...", {
          type: "success",
        });
      });
  };

  const handleRetrieveRecentUpdatedOrders = async () => {
    setLoadingHubspot(true);
    await retrieveRecentUpdatedOrders()
      .then(() => {
        getAllOperations(tableHeaders);
        setLoadingHubspot(false);
        toast.show("Updated Order has been queued for processing...", {
          type: "success",
        });
      })
      .catch((err) => {
        setLoadingHubspot(false);
        toast.show("Updated Order has been queued for processing...", {
          type: "success",
        });
      });
  };

  const SmPageHeader = () => {
    return (
      <View style={styles.container}>
        {/* Header Section */}
        <View style={styles.drawerHeader}>
          <TouchableOpacity
            onPress={() => navigation.toggleDrawer()}
            style={styles.menuButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <MaterialCommunityIcons name="menu" size={24} color="#333" />
          </TouchableOpacity>

          <View style={styles.titleContainer}>
            <Text style={styles.titleText}>Order Status</Text>
          </View>
        </View>

        {/* Content Section */}
        <View style={styles.contentContainer}>
          <View style={[styles.filterSection, { paddingRight: 10, gap: 10 }]}>
            <View style={{ width: "100%" }}>
              <OperationLabelBannerWithSearchTags
                isMobileDevice={isMobileDevice}
                isSmallDevice={isSmallDevice}
                label="Quick Filter"
                noBorder={true}
                filterCategoryList={operationsFilterCategoryList}
                filterCategory={_filterCategory}
                setFilterCategory={_setFilterCategory}
                titleEnabled={true}
                tableHeaders={tableHeaders}
              />
            </View>

            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, width: "100%" }}>
              <TouchableOpacity
                onPress={() => navigation.navigate("PlaceOrder")}
                style={styles.newOrderButton}
              >
                <Text style={styles.newOrderText}>New Order</Text>
                <MaterialCommunityIcons name="plus" size={16} color="#fff" />
              </TouchableOpacity>
              <View style={{ flex: 1 }}>
                <SearchOperationLabelBannerWithSearchBar
                  titleName={`Total Items: ${operationsList.length}`}
                  isSmallDevice={isSmallDevice}
                  isMobileDevice={isMobileDevice}
                  setRefresh={setRefresh}
                  dataToExport={combinedOrders}
                  fileName="orderReportScreen"
                  sheetName="Order Report Screen"
                  disableTitle={true}
                  noBorder={true}
                />
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const DefaultPageHeader = () => {
    return (
      <View style={styles.container}>
        {/* Header Section */}
        <View style={styles.drawerHeader}>
          <TouchableOpacity
            onPress={() => navigation.toggleDrawer()}
            style={styles.menuButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <MaterialCommunityIcons name="menu" size={24} color="#333" />
          </TouchableOpacity>

          <View style={styles.titleContainer}>
            <Text style={styles.titleText}>Order Status</Text>
          </View>
        </View>

        {/* Content Section */}
        <View style={styles.contentContainer}>
          <View style={[styles.filterSection, { gap: 10, paddingRight: 10 }]}>
            <View style={{ width: "100%" }}>
              <OperationLabelBannerWithSearchTags
                isMobileDevice={isMobileDevice}
                isSmallDevice={isSmallDevice}
                label="Quick Filter"
                noBorder={true}
                filterCategoryList={operationsFilterCategoryList}
                filterCategory={_filterCategory}
                setFilterCategory={_setFilterCategory}
                titleEnabled={true}
                tableHeaders={tableHeaders}
              />
            </View>

            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 10, paddingBottom: 10 }}>
              {/* Left Side: Recent & Update Orders */}
              <View
                style={[styles.buttonsWrapper, { flexDirection: "row", gap: 10 }]}
              >
                <Button
                  mode="outlined"
                  onPress={handleRetrieveRecentOrders}
                  style={[styles.actionButton, styles.outlinedButton]}
                  labelStyle={[styles.buttonLabel, styles.outlinedButtonLabel]}
                  contentStyle={styles.buttonContent}
                  disabled={loadingHubspot}
                  uppercase={false}
                >
                  Recent Orders
                </Button>

                <Button
                  mode="contained"
                  onPress={handleRetrieveRecentUpdatedOrders}
                  style={[styles.actionButton, styles.containedButton]}
                  labelStyle={[styles.buttonLabel, styles.containedButtonLabel]}
                  contentStyle={styles.buttonContent}
                  disabled={loadingHubspot}
                  uppercase={false}
                >
                  Update Orders
                </Button>
              </View>

              {/* Right Side: New Order & Search Box */}
              <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1, minWidth: 350, justifyContent: "flex-end" }}>
                <TouchableOpacity
                  onPress={() => navigation.navigate("PlaceOrder")}
                  style={styles.newOrderButton}
                >
                  <Text style={styles.newOrderText}>New Order</Text>
                  <MaterialCommunityIcons name="plus" size={16} color="#fff" />
                </TouchableOpacity>
                <View style={{ flex: 1, maxWidth: 600 }}>
                  <SearchOperationLabelBannerWithSearchBar
                    titleName={`Total Items: ${operationsList.length}`}
                    isSmallDevice={isSmallDevice}
                    isMobileDevice={isMobileDevice}
                    setRefresh={setRefresh}
                    dataToExport={combinedOrders}
                    fileName="orderReportScreen"
                    sheetName="Order Report Screen"
                    disableTitle={true}
                    noBorder={true}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <>
      {modalOpen && selectedEnquiryType === "process_person" && (
        <OperationProcessingUserModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "update_status" && (
        <OperationStatusUpdateModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <ScrollView style={{ flex: 1, backgroundColor: "#fff" }}>
        <PageHeader />
        {!isOperationsListLoaded || loadingHubspot ? (
          <View>
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{
                flexDirection: "row",
                marginTop: "5%",
                marginBottom: "5%",
              }}
            />
          </View>
        ) : (
          <DataTableWithPagination
            headers={tableHeaders}
            nestedHeader={nestedTableHeaders}
            rows={operationsList}
            itemsPerPage={20}
            isDataLoading={isSearched}
            useModal={true}
            isRowsExpandable={true}
            setModalVisible={setModalVisible}
            handleRowClick={onHandleRowClick}
            setSelected={setSelected}
            isToExpandAllRows={_isDisplayViewOn}
            rowKeyValue="_id"
            nestedItemKeyValue="orders"
            isDetail={true}
            isEdit={isUserAdmin}
            isDelete={isUserAdmin}
            columnsToNotHaveThousandSeparator={
              columnsToNotHaveThousandsSeparator
            }
            handleDeleteRow={onHandleDeleteRow}
            handleSortColumn={onHandleSortColumn}
            operationsScreen={false}
            disableHeader={disableHeader}
            operationActions={navigateToOrder}
          />
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  newOrder: {
    color: "#74BBFF",
    fontSize: 12,
  },
  modalButton: {
    borderRadius: 8,
    marginRight: 10,
    minWidth: 160,
    elevation: 2,
  },
  container: {
    backgroundColor: "#fff",
  },

  drawerHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },

  menuButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: "transparent",
  },

  titleContainer: {
    flex: 1,
    paddingLeft: 12,
  },

  titleText: {
    fontSize: 20,
    fontWeight: "600",
    letterSpacing: 0.5,
  },
  newOrderButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E88E5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newOrderText: {
    color: "#fff",
    marginRight: 5,
    fontWeight: "bold",
    cursor: "pointer",
  },

  contentContainer: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },

  filterSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },

  actionsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 60,
  },

  actionsContainerHorizontal: {
    flexDirection: "row",
    alignItems: "center",
  },

  buttonsWrapper: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 20,
    marginBottom: 12,
  },

  buttonsWrapperTablet: {
    gap: 16,
  },

  buttonsWrapperSmall: {
    flexWrap: "nowrap",
    marginRight: 16,
    marginBottom: 0,
  },

  actionButton: {
    borderRadius: 8,
    minWidth: 140,
    elevation: 0,
    shadowOpacity: 0,
  },

  actionButtonSmall: {
    minWidth: 140,
    marginRight: 12,
  },

  outlinedButton: {
    backgroundColor: "#ffffff",
    borderColor: "#E2E8F0",
    borderWidth: 1,
  },

  containedButton: {
    backgroundColor: "#3B82F6",
  },

  buttonContent: {
    paddingHorizontal: 12,
    height: 40,
    justifyContent: "center",
  },

  buttonLabel: {
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
    letterSpacing: 0.2,
  },

  outlinedButtonLabel: {
    color: "#4B5563",
  },

  containedButtonLabel: {
    color: "#ffffff",
  },

  exportWrapper: {
    flex: 1,
    justifyContent: "center",
    minWidth: 200,
  },

  exportWrapperSmall: {
    minWidth: 180,
  },
});
